import React, { useEffect, useRef } from "react";
import { useQuill } from "react-quilljs";
import { toast } from "react-toastify";
import { mediaUrl, uploadImage } from "./contentApi";

// Only what the storefront keeps (the API sanitiser drops everything else):
// headings, emphasis, lists, quotes, links, images, alignment.
const MODULES = {
  toolbar: [
    [{ header: [2, 3, 4, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link", "image"],
    [{ align: [] }],
    ["clean"],
  ],
};
const FORMATS = ["header", "bold", "italic", "underline", "strike", "list", "bullet", "blockquote", "link", "image", "align"];

/**
 * Controlled-ish Quill editor: `value` is loaded once when the editor is
 * ready (and again when `resetKey` changes, e.g. after "reset to default");
 * every edit reports HTML through onChange. Images upload to the API and are
 * inserted with their full URL so the storefront can show them as-is.
 */
export default function RichTextEditor({ value, onChange, resetKey, placeholder = "Write here…", minHeight = 320 }) {
  const { quill, quillRef } = useQuill({ modules: MODULES, formats: FORMATS, placeholder });
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Load content (initially, and when the parent resets it).
  useEffect(() => {
    if (!quill) return;
    quill.clipboard.dangerouslyPasteHTML(value || "");
    quill.history.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on ready / explicit reset
  }, [quill, resetKey]);

  useEffect(() => {
    if (!quill) return;
    const handler = () => {
      const html = quill.root.innerHTML;
      onChangeRef.current(html === "<p><br></p>" ? "" : html);
    };
    quill.on("text-change", handler);

    quill.getModule("toolbar").addHandler("image", () => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/jpeg,image/png,image/webp,image/avif";
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        try {
          const path = await uploadImage(file);
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, "image", mediaUrl(path), "user");
          quill.setSelection(range.index + 1);
        } catch (err) {
          toast.error(err.message);
        }
      };
      input.click();
    });

    return () => quill.off("text-change", handler);
  }, [quill]);

  return (
    <div className="bg-white">
      <div ref={quillRef} style={{ minHeight }} />
    </div>
  );
}
